import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Loader2, Download, ShieldCheck, Filter, Check, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const STATUS_COLOR = {
  submitted: "bg-blue-100 text-blue-800 border-blue-200",
  reviewed: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const CATEGORY_LABEL = {
  performer: "Performer",
  restaurant: "Restaurant / Catering",
  trader: "Trader / Services",
  business_matching: "Business Matching",
  sponsor: "Sponsor",
};

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [nextStatus, setNextStatus] = useState("approved");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/registrations", {
          params: {
            ...(filterCat !== "all" && { category: filterCat }),
            ...(filterStatus !== "all" && { status: filterStatus }),
          },
        }),
      ]);
      setStats(statsRes.data);
      setItems(listRes.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterCat, filterStatus]);

  const openReview = (reg) => {
    setSelected(reg);
    setReviewNote(reg.admin_note || "");
    setNextStatus(reg.status === "submitted" ? "approved" : reg.status);
  };

  const submitReview = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/registrations/${selected.id}/status`, {
        status: nextStatus,
        admin_note: reviewNote,
      });
      toast.success(`Registration ${nextStatus}. Notification email queued.`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const downloadCsv = () => {
    // Trigger direct download (session cookie is credentials: 'include')
    const url = `${API}/admin/registrations.csv`;
    window.open(url, "_blank");
  };

  if (loading || !user) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
      </div>
    );
  }
  if (!user.is_admin) return null;

  const statCards = stats
    ? [
        { label: "Total", value: stats.total },
        { label: "Submitted", value: stats.by_status.submitted || 0 },
        { label: "Approved", value: stats.by_status.approved || 0 },
        { label: "Paid", value: stats.by_payment_status.paid || 0 },
      ]
    : [];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-5 h-5 text-brand-red" />
        <div className="text-xs uppercase tracking-widest text-brand-red font-semibold">Admin Panel</div>
      </div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="admin-title">
          Registrations · {user.email}
        </h1>
        <Button onClick={downloadCsv} className="rounded-full gap-2" data-testid="admin-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white border border-border rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className="font-display text-3xl font-bold mt-1" data-testid={`admin-stat-${s.label.toLowerCase()}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-56" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead className="bg-brand-canvas/70">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">ID</th>
                <th className="text-left px-5 py-3 font-semibold">Category</th>
                <th className="text-left px-5 py-3 font-semibold">Applicant</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Payment</th>
                <th className="text-left px-5 py-3 font-semibold">Submitted</th>
                <th className="text-right px-5 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {items === null ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No registrations found.</td></tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-brand-canvas/30" data-testid={`admin-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono text-xs">{r.id}</td>
                    <td className="px-5 py-3">{CATEGORY_LABEL[r.category] || r.category}</td>
                    <td className="px-5 py-3">
                      <div className="font-medium">{r.user_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{r.user_email}</div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge className={`${STATUS_COLOR[r.status]} border font-medium`}>{r.status}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant="outline">{r.payment_status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openReview(r)} data-testid={`admin-review-${r.id}`}>
                        Review <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Review dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Review Registration
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground">ID</div><div className="font-mono text-xs">{selected.id}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Category</div><div>{CATEGORY_LABEL[selected.category]}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Applicant</div><div>{selected.user_name} · {selected.user_email}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Submitted</div><div>{new Date(selected.created_at).toLocaleString()}</div></div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Application data</div>
                <pre className="bg-brand-canvas rounded-lg p-3 text-xs overflow-x-auto max-h-56">
                  {JSON.stringify(selected.data, null, 2)}
                </pre>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Change status to</div>
                <Select value={nextStatus} onValueChange={setNextStatus}>
                  <SelectTrigger data-testid="review-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Admin note (sent in email)</div>
                <Textarea
                  rows={3}
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Optional note to the applicant"
                  data-testid="review-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button
              onClick={submitReview}
              disabled={saving}
              className={`gap-2 rounded-full ${nextStatus === "rejected" ? "bg-red-600 hover:bg-red-700" : "bg-brand-red hover:bg-brand-red-dark"} text-white`}
              data-testid="review-submit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> :
                (nextStatus === "rejected" ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />)}
              Save & notify applicant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
